import { FC, useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";

import { CategoryIconKey } from "../../config/categories";
import { CategoryIcon, IconArrowRight } from "../../ui/icons";

const TITLE_COLLAPSED = 21;
/* portal-selection.css'teki `min-height` ile birlikte değişmeli: bütün kartlar aynı boyda. */
const DESC_COLLAPSED = 84;

/** Kapanışta tek satıra dönüş geciktirilir; yoksa metin anında sıçrar. */
const CLOSE_DELAY_MS = 340;

interface Props {
  name: string;
  description: string;
  icon: CategoryIconKey;
  categoryLabel: string;
  /** Kişiselleştirme modunda kart açılmaz ve metin genişlemesi durur; kullanıcı sürüklüyor. */
  isCustomizing: boolean;
  /** Her çağrıda yeniden üretilir; gerekçesi `onMouseDown`da. */
  buildHref: () => string;
  onOpen: () => void;
}

const PortalCard: FC<Props> = ({
  name,
  description,
  icon,
  categoryLabel,
  isCustomizing,
  buildHref,
  onOpen,
}) => {
  const anchorRef = useRef<HTMLAnchorElement>(null);
  const titleRef = useRef<HTMLHeadingElement>(null);
  const descRef = useRef<HTMLParagraphElement>(null);
  const closeTimer = useRef<number | null>(null);

  const [isHovered, setIsHovered] = useState(false);
  const [isWrapped, setIsWrapped] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [expandedHeights, setExpandedHeights] = useState<{
    title: number;
    desc: number;
  }>({ title: TITLE_COLLAPSED, desc: DESC_COLLAPSED });

  // Maske yalnızca metin gerçekten kırpılmışsa uygulanır.
  const [overflow, setOverflow] = useState({ title: false, desc: false });

  useLayoutEffect(() => {
    if (isWrapped) return; // ölçüm kapalı haldeyken yapılır
    const title = titleRef.current;
    const desc = descRef.current;
    setOverflow({
      title: !!title && title.scrollWidth > title.clientWidth + 1,
      desc: !!desc && desc.scrollHeight > DESC_COLLAPSED + 1,
    });
  }, [name, description, isWrapped]);

  // Önce sarma açılır, sonra gerçek yükseklik ölçülüp ona animasyon yapılır.
  useLayoutEffect(() => {
    if (!isWrapped || !isHovered || isExpanded) return;
    setExpandedHeights({
      title: titleRef.current?.scrollHeight || TITLE_COLLAPSED,
      desc: descRef.current?.scrollHeight || DESC_COLLAPSED,
    });
    setIsExpanded(true);
  }, [isWrapped, isHovered, isExpanded]);

  const clearCloseTimer = () => {
    if (closeTimer.current !== null) {
      window.clearTimeout(closeTimer.current);
      closeTimer.current = null;
    }
  };

  useEffect(() => clearCloseTimer, []);

  const onEnter = useCallback(() => {
    if (isCustomizing) return;
    clearCloseTimer();
    setIsHovered(true);
    setIsWrapped(true);
  }, [isCustomizing]);

  const onLeave = useCallback(() => {
    setIsHovered(false);
    setIsExpanded(false);
    clearCloseTimer();
    closeTimer.current = window.setTimeout(
      () => setIsWrapped(false),
      CLOSE_DELAY_MS
    );
  }, []);

  /*
   * Adres varken kart bir `a`: orta tık ve sağ tık "yeni sekmede aç" yalnızca gerçek
   * bir bağlantıda çalışır. Kişiselleştirme modunda ve redirectUri tanımsızken adres
   * yok, o durumda `button` dönülür; adressiz bir `a` klavyeyle erişilemez olurdu.
   */
  const href = isCustomizing ? "" : buildHref();

  const body = (
    <>
      <div className="sso-rd__portal-top">
        <span className="sso-rd__icon-tile">
          <CategoryIcon icon={icon} size={21} />
        </span>

        <div className="sso-rd__portal-text">
          <div className="sso-rd__portal-title-row">
            <h3
              ref={titleRef}
              className={
                "sso-rd__portal-title" +
                (!isExpanded && overflow.title ? " is-clipped" : "")
              }
              style={{
                maxHeight: isExpanded ? expandedHeights.title : TITLE_COLLAPSED,
                whiteSpace: isWrapped ? "normal" : "nowrap",
              }}
            >
              {name}
            </h3>
            <IconArrowRight size={19} className="sso-rd__portal-arrow" />
          </div>

          <p
            ref={descRef}
            className={
              "sso-rd__portal-desc" +
              (!isExpanded && overflow.desc ? " is-clipped" : "")
            }
            style={{ maxHeight: isExpanded ? expandedHeights.desc : DESC_COLLAPSED }}
          >
            {description}
          </p>
        </div>
      </div>

      <div className="sso-rd__portal-foot">
        <span className="sso-rd__chip">{categoryLabel}</span>
      </div>
    </>
  );

  const shared = {
    className: "sso-rd__portal-card",
    onMouseEnter: onEnter,
    onMouseLeave: onLeave,
    onFocus: onEnter,
    onBlur: onLeave,
  };

  if (!href) {
    return (
      <button type="button" {...shared} aria-disabled={isCustomizing}>
        {body}
      </button>
    );
  }

  return (
    <a
      {...shared}
      ref={anchorRef}
      href={href}
      /*
       * Adres basılma anında tazelenir. Vekaleten bir oturum sayfa yenilenmeden
       * sonlandırılabiliyor (ProfileSwitcher `end`), o durumda render sırasında
       * hesaplanan sessionKey bayatlar ve portal YANLIŞ kimlikle açılırdı.
       */
      onMouseDown={() => {
        if (anchorRef.current) anchorRef.current.href = buildHref();
      }}
      onClick={(event) => {
        // Yeni sekme / yeni pencere isteği: tarayıcı kendi işini yapsın.
        if (event.ctrlKey || event.metaKey || event.shiftKey || event.altKey) return;

        // Düz sol tık aynı sekmede açar ve hesabı köke döndürür.
        event.preventDefault();
        onOpen();
      }}
    >
      {body}
    </a>
  );
};

export default PortalCard;
